import React, { useEffect, useState } from 'react';
import './LandingPage.css';
import LandingNav from '../components/landing/LandingNav';
import PlatformCompanyInfo from '../components/landing/PlatformCompanyInfo';
import { listSchoolNotices } from '../services/schoolNoticeService';
import type { SchoolNoticeRow } from '../types/schoolNotice';
import { sanitizeSchoolNoticeHtml } from '../utils/schoolNoticeHtml';
import { loadSchoolNoticesCache } from '../utils/platformGlobalAdminCache';

interface LandingNoticesPageProps {
  onLoginClick: () => void;
}

function formatNoticeDate(iso: string): string {
  const raw = (iso ?? '').trim();
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

const LandingNoticesPage: React.FC<LandingNoticesPageProps> = ({ onLoginClick }) => {
  const [items, setItems] = useState<SchoolNoticeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const cached = loadSchoolNoticesCache();
        if (cached.found && cached.rows.length) {
          const sorted = [...cached.rows].sort((a, b) =>
            String(b.updated_at).localeCompare(String(a.updated_at))
          );
          setItems(sorted);
          setLoading(false);
        }
        const fresh = await listSchoolNotices();
        if (cancelled) return;
        const sorted = [...fresh].sort((a, b) =>
          String(b.updated_at).localeCompare(String(a.updated_at))
        );
        setItems(sorted);
        setError(null);
        setLoading(false);
      } catch (err) {
        console.error('공지사항 로드 실패:', err);
        if (!cancelled) {
          if (!loadSchoolNoticesCache().found) {
            setError('공지사항을 불러올 수 없습니다.');
          }
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="landing-page landing-page--loading">
        <div className="landing-loading-spinner" aria-label="로딩 중" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page landing-page--error">
        <LandingNav onLoginClick={onLoginClick} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="landing-page landing-qa-page landing-notices-page">
      <LandingNav onLoginClick={onLoginClick} />

      <main className="landing-qa-main">
        <header className="landing-qa-header">
          <h1 className="landing-qa-title">공지사항</h1>
          <p className="landing-qa-lead">클래스 플래너 소식과 안내를 확인하세요.</p>
        </header>

        <div className="landing-qa-list" role="list">
          {items.length === 0 ? (
            <p className="landing-qa-empty">등록된 공지사항이 없습니다.</p>
          ) : (
            items.map((item) => {
              const isOpen = openId === item.id;
              const dateLabel = formatNoticeDate(item.updated_at || item.created_at);
              const safeHtml = sanitizeSchoolNoticeHtml(item.body_html);
              return (
                <div
                  key={item.id}
                  className={`landing-qa-item${isOpen ? ' is-open' : ''}`}
                  role="listitem"
                >
                  <button
                    type="button"
                    className="landing-qa-question"
                    aria-expanded={isOpen}
                    onClick={() => setOpenId(isOpen ? null : item.id)}
                  >
                    <span className="landing-qa-question-text">
                      {dateLabel ? (
                        <span className="landing-qa-category-tag">{dateLabel}</span>
                      ) : null}
                      <span className="landing-qa-question-label">
                        {(item.title ?? '').trim() || '공지사항'}
                      </span>
                    </span>
                    <span className="landing-qa-chevron" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                        <path
                          d="M6 9l6 6 6-6"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  {isOpen ? (
                    <div className="landing-qa-answer landing-notices-answer">
                      <div
                        className="landing-notices-answer-html cp-school-notice-html"
                        dangerouslySetInnerHTML={{
                          __html:
                            safeHtml ||
                            '<p class="landing-qa-answer-text">내용이 없습니다.</p>',
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </main>

      <PlatformCompanyInfo reveal={false} />
    </div>
  );
};

export default LandingNoticesPage;
